/**
 * A SearchRule model holds the data the makes up a single search rule; A SearchRules collection contains 1-10 of these.
 *
 * @param {Array} elementType Integers describing the search types; see search/properties/search.
 * @param {Array} elementSubType1 Integers describing the search subtypes; search/properties/search.
 * @param {Array} elementSubType2 Integers describing the search subtypes; search/properties/search.
 * @param {Array} elementValue1 takes an array of Strings.
 * @param {Array} elementValue2 takes an array of Strings.
 * @param {Array} elementValue3 takes an array of Strings.
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import properties from '../properties/search';

export default class SearchRule extends Base {
	/**
	 * What search type are we dealing with
	 * Fall back in case anyone tries to use this model without specifying it is for assets
	 * @type {string}
	 * @default
	 */
	get searchApi() {
		return 'assets';
	}

	/** @type {object} */
	get defaults() {
		return {
			// Type
			// SEARCH_TYPE_KEYWORDS           - 1
			// SEARCH_TYPE_CONTENTS           - 2
			// SEARCH_TYPE_METADATA           - 3
			// SEARCH_TYPE_DATE               - 4
			// SEARCH_TYPE_CATEGORY           - 5 (Path string)
			// SEARCH_TYPE_FILE_FORMAT        - 6
			// SEARCH_TYPE_FILE_SIZE          - 7
			// SEARCH_TYPE_RAW                - 8
			// SEARCH_TYPE_CUSTOM             - 9
			// SEARCH_TYPE_CART               - 10
			// SEARCH_TYPE_RELATED_ASSETS     - 11
			// SEARCH_TYPE_LAST_SEARCH        - 12
			// SEARCH_TYPE_CHECKOUT           - 13 ( Value1 = userId; zero = all )
			// SEARCH_TYPE_THESAURUS          - 14
			// SEARCH_TYPE_BRANCH_CHILDREN    - 15
			// SEARCH_TYPE_REVIEWS            - 16 (Contextual to user)
			// SEARCH_TYPE_EXPIRE             - 17 (All)
			// SEARCH_TYPE_METADATA_HISTORY   - 18
			// SEARCH_TYPE_RATING             - 19
			// SEARCH_TYPE_LOCATION           - 20 (Storage location)
			// SEARCH_TYPE_PROOF              - 21 (For showing proofs)
			// SEARCH_TYPE_FILE_ASPECT        - 22
			// SEARCH_TYPE_CATEGORY_ID        - 23 (value1 must be an integer of the Category ID)
			// SEARCH_TYPE_SAVED_SEARCH       - 24 (value1 must be an integer of the Saved Search ID)
			// SEARCH_TYPE_FILE_STATUS        - 25 (value1 must be an integer of the status type)
			// SEARCH_TYPE_FILE_FORMAT_FAMILY - 26 (value1 must be one of the known families (from New York or Jersey); see FormatFamilyManager)
			// SEARCH_TYPE_VISUAL             - 27
			// SEARCH_TYPE_USER               - 29
			elementType: 0,

			// Sub type 1
			//
			// Keyword/Contents/Metadata
			// QUERY_TYPE_AND                - 0
			// QUERY_TYPE_EXACT              - 1
			// QUERY_TYPE_OR                 - 2
			// QUERY_TYPE_NOT                - 3
			// QUERY_TYPE_AND_FRAG           - 4
			// QUERY_TYPE_OR_FRAG            - 5
			// QUERY_TYPE_RANGE              - 6
			// QUERY_TYPE_PHRASE             - 7
			// QUERY_TYPE_RAW                - 8
			// QUERY_TYPE_EMPTY              - 9
			// QUERY_TYPE_RESPECT_DIACRITICS - 10
			//
			// Category
			// CATEGORY_TYPE_ONLY_RECURSIVE    - 0
			// CATEGORY_TYPE_EXCLUDE_RECURSIVE - 1
			// CATEGORY_TYPE_ONLY              - 2
			// CATEGORY_TYPE_EXCLUDE           - 3
			// CATEGORY_TYPE_RECURSIVE         - 4
			//
			// Checkout
			// CHECKOUT_TYPE_ANY  - 0
			// CHECKOUT_TYPE_USER - 1
			//
			// Date
			// DATE_TYPE_CREATION     - 0
			// DATE_TYPE_MODIFICATION - 1
			// DATE_TYPE_ATTRIBUTE    - 2
			// DATE_TYPE_IMPORT       - 3
			//
			// File aspect
			// FILE_ASPECT_TYPE_ORIENTATION - 0
			// FILE_ASPECT_TYPE_DURATION    - 1
			// FILE_ASPECT_TYPE_WIDTH       - 2
			// FILE_ASPECT_TYPE_HEIGHT      - 3
			//
			// File-Format
			// TYPE_ONLY    - 0
			// TYPE_EXCLUDE - 1
			//
			// File Size
			// FILE_SIZE_TYPE_APPROXIMATELY - 0
			// FILE_SIZE_TYPE_GREATER_THAN  - 1
			// FILE_SIZE_TYPE_LESS_THAN     - 2
			// FILE_SIZE_TYPE_EQUAL_TO      - 3
			//
			// Rating
			// RATING_TYPE_APPROXIMATELY - 0
			// RATING_TYPE_GREATER_THAN  - 1
			// RATING_TYPE_LESS_THAN     - 2
			// RATING_TYPE_EQUAL_TO      - 3
			//
			// Visual
			// VISUAL_TYPE_ID    - 1
			// VISUAL_TYPE_COLOR - 2
			//
			// User
			// USER_TYPE_IMPORT - 0
			// USER_TYPE_MOD 	- 1
			elementSubType1: 0,

			// Sub type 2
			//
			// Date
			// DATE_ANYTIME           - 0
			// DATE_LAST_THREE_MONTHS - 1
			// DATE_LAST_SIX_MONTHS   - 2
			// DATE_LAST_YEAR         - 3
			// DATE_BEFORE            - 4
			// DATE_AFTER             - 5
			// DATE_INBETWEEN         - 6
			// DATE_LAST_WEEK         - 7
			// DATE_LAST_MONTH        - 8
			// DATE_LAST_DAY          - 9
			// DATE_YESTERDAY         - 10
			// DATE_LAST_TWO_MONTHS   - 11
			//
			// File Aspect
			// FILE_ASPECT_TYPE_APPROXIMATELY - 0
			// FILE_ASPECT_TYPE_GREATER_THAN  - 1
			// FILE_ASPECT_TYPE_LESS_THAN     - 2
			// FILE_ASPECT_TYPE_EQUAL_TO      - 3
			//
			// File Size
			// FILE_SIZE_SIZE_BYTES  - 0
			// FILE_SIZE_SIZE_KBYTES - 1
			// FILE_SIZE_SIZE_MBYTES - 2
			// FILE_SIZE_SIZE_GBYTES - 3
			elementSubType2: 0,

			// TODO finish documenting values
			// Value 1
			// Date         - Date
			// Date (range) - Start date
			// Metadata     - Attribute template name
			elementValue1: '',
			// Value 2
			// Date (range) - End date
			// Metadata     - Attribute value
			elementValue2: '',
			// Value 3
			elementValue3: '',
		};
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts) {
		super(attrs, opts);

		/**
		 * Search properties to use
		 * @memberOf Netx.Models.SearchRule
		 * @alias searchProps
		 * @type {object}
		 */
		this.searchProps = properties[this.searchApi];
	}

	/**
	 * @method
	 * @param {boolean} ignoreQuirks Ignore quirks (get a pure rule and not one mangled for the quirks of the sALO params we pass to the server)
	 * @return {object} Search object
	 */
	getSearchObject(ignoreQuirks) {
		return this.self();
	}

	/**
	 * We never sync with the server (not currently anyways)
	 * @override
	 * @return {boolean}
	 */
	isNew() {
		return false;
	}

	/**
	 * @deparcated - no longer used
	 * Utility function for looking up the names of type properties that are stored as integers,
	 * and mapped awkwardly in search/properties/search
	 * @method
	 * @param {number} type Integer representation of type; if left out, use this model's type.
	 * @return {string}
	 */
	typeName(type) {
		type = type || this.get('elementType');

		var thing = _.findWhere(this.searchProps.elementTypes.options, {val: type});

		return thing ? thing.label : undefined;
	}

	/**
	 * @deparcated - no longer used
	 * Return the name of this model's subtype1 field.
	 * @method
	 * @return {string}
	 */
	subType1Name() {
		var type = this.get('elementType');
		if (_.isUndefined(this.searchProps.elementSubType1s[type].options)) {
			return undefined;
		}
		var thing = _.findWhere(this.searchProps.elementSubType1s[type].options, {
			val: this.get('elementSubType1'),
		});

		return thing ? thing.label : undefined;
	}

	/**
	 * @deparcated - no longer used
	 * Return the name of this model's subtype2 field.
	 * @method
	 * @return {string}
	 */
	subType2Name() {
		var type = this.get('elementType');
		if (_.isUndefined(this.searchProps.elementSubType2s[type].options)) {
			return undefined;
		}
		var thing = _.findWhere(this.searchProps.elementSubType2s[type].options, {
			val: this.get('elementSubType2'),
		});

		return thing ? thing.label : undefined;
	}

	/**
	 * Is this rule searching on dynamic metadata?
	 * (as opposed to system metadata or any other type of search.)
	 *
	 * @returns boolean
	 *
	 * @method
	 */
	searchTargetIsMetadata() {
		return null;
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchRule';
	}
}
